import { motion, AnimatePresence, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { useRef, useState } from "react";
import { Linkedin } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Reveal helper                                                       */
/* ------------------------------------------------------------------ */
const R = ({ children, d = 0, c = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: d, ease: [0.16, 1, 0.3, 1] }}
    className={c}
  >
    {children}
  </motion.div>
);

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */
const PILLARS = [
  {
    n: "01",
    tag: "OUR VISION",
    title: "A Life Worth Living.",
    body:
      "Waking before your alarm because sleep finally did its work. Climbing four flights and still having breath for a hello. A Sunday walk that turns into a long one because the knees feel good again. Wellness stops being a task the day your body starts giving you reasons to keep going. That is the life Trifit is built for.",
    image:
      "https://images.unsplash.com/photo-1519614218660-ea0a24a43b4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxtb3VudGFpbiUyMHNpbGhvdWV0dGUlMjBkYXdufGVufDB8fHxibGFja3wxNzg2OTQ3MzkxfDA&ixlib=rb-4.1.0&q=85",
    alt: "Layered mountain silhouettes at dawn, a life worth living",
    reverse: false,
  },
  {
    n: "02",
    tag: "OUR MISSION",
    title: "To Move People Forward.",
    body:
      "You already know what to do. Sleep earlier. Move more. Eat cleaner. Drink less. Knowing has never been the problem. Following through on a Tuesday when the day fell apart is. Trifit is the small nudge at the right moment that turns knowing into doing, one honest week at a time.",
    image:
      "https://images.unsplash.com/photo-1660390283642-05030c2c2aa4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxoYW5kcyUyMG1lZXRpbmclMjB0YWJsZXxlbnwwfHx8YmxhY2t8MTc4Njk0NzQwM3ww&ixlib=rb-4.1.0&q=85",
    alt: "Hands planning and collaborating, moving people forward",
    reverse: true,
  },
  {
    n: "03",
    tag: "OUR PROMISE",
    title: "We Will Never Sell Your Data. Ever.",
    body:
      "Your body's data belongs to you. Your sleep, your steps, your bloodwork, the honest logs about the wine or the missed workout. None of it is packaged for advertisers. None of it is sold to insurers. Trifit exists because that promise had to exist, and we built the whole company on that one line.",
    image:
      "https://images.unsplash.com/photo-1601290884789-47a421212270?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxsYW50ZXJuJTIwZGFya25lc3N8ZW58MHx8fGJsYWNrfDE3ODY5NDc0MTh8MA&ixlib=rb-4.1.0&q=85",
    alt: "Lone lantern glowing in darkness, an unbreakable promise",
    reverse: false,
  },
];

const TEAM = [
  { name: "Shiv Pardal",           role: "Founder & CEO" },
  { name: "Nigel Whan",            role: "Founder & Product Manager" },
  { name: "Nimrod Mon Brokman",    role: "Founder & Head of Science" },
  { name: "Nisha Agarwal",         role: "Operations Manager" },
  { name: "Sumidha Kumari Prasad", role: "Data Analyst" },
  { name: "Shivam Prakash Bharti", role: "Project Manager" },
  { name: "Jitendra Ghadei",       role: "VP of Engineering" },
  { name: "Ravi Shukla",           role: "VP of Engineering" },
  { name: "Pujan Sharma",          role: "Software Engineer" },
  { name: "Shudhanshu Shekar",     role: "Software Engineer" },
  { name: "Vivek Basker",          role: "Designer & Video Editor" },
];

const ADVISORS = [
  { name: "Rishi Pardal",       role: "Business Advisor", kind: "person" },
  { name: "IIT-Guwahati Bionest", role: "Incubator",       kind: "org", tagline: "INCUBATE · NURTURE · ACCELERATE" },
];

const initialsOf = (n) =>
  n
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const testIdOf = (n) =>
  n
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/* ------------------------------------------------------------------ */
/* Member card · monogram, minimal, on-brand                           */
/* ------------------------------------------------------------------ */
const MemberCard = ({ name, role, accent = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`about-member-${testIdOf(name)}`}
    className="group relative flex flex-col overflow-hidden rounded-xl border border-line bg-ink-2/40 transition-colors duration-500 hover:border-signal/50"
  >
    {/* Monogram plate · square, compact */}
    <div className="relative flex aspect-square items-center justify-center overflow-hidden bg-gradient-to-b from-ink-2 to-ink border-b border-line">
      {/* subtle grid texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      {/* corner tick · restrained, just TRIFIT wordmark */}
      <span className="absolute left-2.5 top-2.5 font-mono text-[8px] tracking-[0.24em] text-ash/70">TRIFIT</span>

      {/* Monogram */}
      <div className="relative">
        <span
          className={`font-display text-5xl font-light tracking-tight sm:text-6xl ${accent ? "text-signal" : "text-white"}`}
        >
          {initialsOf(name)}
        </span>
        <motion.span
          aria-hidden="true"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="absolute -bottom-2 left-0 block h-px w-full origin-left bg-signal/70"
        />
      </div>
    </div>

    {/* Text row */}
    <div className="flex items-center justify-between gap-3 px-3 py-3">
      <div className="min-w-0">
        <p className="truncate font-display text-sm font-light text-white sm:text-base">{name}</p>
        <p className="mt-1 truncate font-mono text-[9px] tracking-[0.18em] text-ash">
          {role.toUpperCase()}
        </p>
      </div>
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line text-fog transition-colors duration-300 group-hover:border-signal/60 group-hover:text-signal"
      >
        <Linkedin className="h-3 w-3" />
      </span>
    </div>
  </motion.div>
);

/* ------------------------------------------------------------------ */
/* Advisor card · differentiated (person vs org)                       */
/* ------------------------------------------------------------------ */
const AdvisorCard = ({ name, role, kind, tagline }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`about-advisor-${testIdOf(name)}`}
    className="group relative overflow-hidden rounded-xl border border-line bg-ink-2/50 p-8 transition-colors duration-500 hover:border-signal/50 md:p-10"
  >
    <div className="flex items-start gap-6">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-signal/40 bg-ink font-display text-2xl font-light text-signal">
        {kind === "org" ? "∞" : initialsOf(name)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[10px] tracking-[0.24em] text-ash">
          {kind === "org" ? "PARTNER · INCUBATOR" : "ADVISOR"}
        </p>
        <h4 className="mt-3 font-display text-2xl font-light text-white md:text-3xl">
          {name}
        </h4>
        <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-fog">
          {role.toUpperCase()}
        </p>
        {tagline && (
          <p className="mt-6 font-mono text-[10px] tracking-[0.3em] text-signal/80">
            {tagline}
          </p>
        )}
      </div>
    </div>
    {/* accent hairline */}
    <span
      aria-hidden="true"
      className="absolute inset-x-8 bottom-0 h-px origin-left scale-x-0 bg-signal/70 transition-transform duration-500 group-hover:scale-x-100"
    />
  </motion.div>
);

/* ------------------------------------------------------------------ */
/* Pillars · pinned narrative                                           */
/* Vision → Mission → Promise fade through as user scrolls;              */
/* wide banner image crossfades to match the active pillar.              */
/* Whole section fits within one laptop screen at any moment.           */
/* ------------------------------------------------------------------ */
const PillarsPinned = ({ pillars }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const [idx, setIdx] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    // 3 bands: [0, 0.33], [0.33, 0.66], [0.66, 1]
    const next = v < 0.34 ? 0 : v < 0.67 ? 1 : 2;
    if (next !== idx) setIdx(next);
  });

  const barY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      ref={ref}
      data-testid="about-pillars"
      className="relative border-t border-line/60 bg-ink-2/40"
      style={{ height: "300vh" }}
    >
      <div className="sticky top-0 flex h-screen items-center overflow-hidden px-6 md:px-12">
        <div className="mx-auto grid h-full max-h-[720px] w-full max-w-[1500px] grid-rows-[auto_1fr_auto] gap-6 py-16 md:gap-8 md:py-20">
          {/* Top row: eyebrow + progress */}
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
                WHAT WE STAND FOR
              </p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.24em] text-signal">
                {String(idx + 1).padStart(2, "0")} / 03 ·{" "}
                {pillars[idx].tag.replace("OUR ", "")}
              </p>
            </div>
            {/* step dots */}
            <div className="flex items-center gap-3" data-testid="about-pillar-dots">
              {pillars.map((p, i) => (
                <span
                  key={p.n}
                  data-testid={`about-pillar-dot-${p.n}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === idx ? "w-8 bg-signal" : "w-4 bg-line"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Banner image · crossfade */}
          <div className="relative w-full overflow-hidden rounded-2xl border border-line">
            <div
              className="relative w-full"
              style={{ aspectRatio: "24 / 7", maxHeight: "36vh" }}
            >
              {pillars.map((p, i) => (
                <motion.img
                  key={p.n}
                  src={p.image}
                  alt={p.alt}
                  data-testid={`about-pillar-image-${p.n}`}
                  initial={false}
                  animate={{ opacity: i === idx ? 1 : 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
              ))}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ink/60 via-ink/10 to-ink/50" />
              <div className="pointer-events-none absolute left-4 top-4 rounded-md border border-line bg-ink/70 px-3 py-1.5 backdrop-blur-sm">
                <p className="font-mono text-[9px] tracking-[0.28em] text-ash">
                  FIG {pillars[idx].n}
                </p>
              </div>
            </div>
          </div>

          {/* Text block · crossfade */}
          <div className="relative min-h-[190px] md:min-h-[210px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={pillars[idx].n}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                className="grid gap-6 lg:grid-cols-12 lg:gap-12"
                data-testid={`about-pillar-${testIdOf(pillars[idx].tag)}`}
              >
                <div className="lg:col-span-5">
                  <h2
                    data-testid={`about-pillar-title-${pillars[idx].n}`}
                    className="font-display text-3xl font-light leading-tight text-signal md:text-5xl"
                  >
                    {pillars[idx].tag
                      .toLowerCase()
                      .replace(/(^|\s)\S/g, (t) => t.toUpperCase())}
                  </h2>
                  <p className="mt-3 font-display text-lg font-light leading-snug text-white md:text-xl">
                    {pillars[idx].title}
                  </p>
                </div>
                <div className="lg:col-span-7">
                  <p className="max-w-2xl text-sm leading-relaxed text-fog md:text-base">
                    {pillars[idx].body}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Right rail progress bar (desktop only) */}
        <div className="pointer-events-none absolute right-4 top-1/2 hidden h-40 w-px -translate-y-1/2 bg-line lg:block">
          <motion.div
            style={{ height: barY }}
            className="w-px origin-top bg-signal"
          />
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
const About = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <main data-testid="about-page" className="relative z-10 text-white">
      {/* 01 · INTRO */}
      <section
        ref={heroRef}
        data-testid="about-hero"
        className="relative flex min-h-[92vh] items-center overflow-hidden pt-32"
      >
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 md:px-12">
          <motion.div style={{ y: titleY, opacity: titleOpacity }}>
            <p
              data-testid="about-eyebrow"
              className="font-mono text-[11px] tracking-[0.28em] text-ash"
            >
              ABOUT · TRIFIT
              <span className="mx-4 text-line">/</span>
              THE PEOPLE BEHIND THE MODEL
            </p>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              data-testid="about-hero-headline"
              className="mt-10 max-w-4xl font-display text-5xl font-light leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl"
            >
              A small team building a{" "}
              <span className="text-signal">personal intelligence</span> system
              for how humans actually live.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="mt-10 max-w-xl text-base leading-relaxed text-fog"
            >
              Three things a digital twin cannot borrow from anyone else: a
              vision worth building, a mission that keeps us honest, and a
              promise to every person whose signals we hold.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="mt-14 flex items-center gap-4"
            >
              <span className="font-mono text-[9px] tracking-[0.22em] text-ash">
                SCROLL ↓
              </span>
              <span className="h-px w-24 bg-line" />
              <span className="font-mono text-[9px] tracking-[0.22em] text-signal">
                VISION / MISSION / PROMISE
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 02 · PILLARS (Vision / Mission / Promise) · pinned scroll narrative */}
      <PillarsPinned pillars={PILLARS} />

      {/* 03 · TEAM */}
      <section
        data-testid="about-team"
        className="relative px-6 py-24 md:px-12 md:py-32"
      >
        <div className="mx-auto max-w-[1500px]">
          <R>
            <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
              04 · THE TEAM
            </p>
            <div className="mt-6 flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
              <h2
                data-testid="about-team-headline"
                className="max-w-3xl font-display text-4xl font-light leading-tight text-white md:text-6xl"
              >
                The people behind{" "}
                <span className="text-signal">the model.</span>
              </h2>
              <p className="max-w-md text-base leading-relaxed text-fog">
                Founders, scientists, engineers and operators · a{" "}
                <span className="text-white">Bangalore-based</span> team
                building Trifit end-to-end, incubated at IIT Guwahati.
              </p>
            </div>
          </R>

          <div
            data-testid="about-team-grid"
            className="mt-16 grid gap-4 md:gap-5"
            style={{
              gridTemplateColumns:
                "repeat(auto-fit, minmax(180px, 1fr))",
            }}
          >
            {TEAM.map((m, i) => (
              <MemberCard
                key={m.name}
                name={m.name}
                role={m.role}
                accent={i < 3}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 04 · ADVISORS */}
      <section
        data-testid="about-advisors"
        className="relative border-t border-line/60 bg-ink-2/40 px-6 py-24 md:px-12 md:py-32"
      >
        <div className="mx-auto max-w-[1500px]">
          <R>
            <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
              05 · ADVISORS & PARTNERS
            </p>
            <h2
              data-testid="about-advisors-headline"
              className="mt-6 max-w-3xl font-display text-4xl font-light leading-tight text-white md:text-6xl"
            >
              Standing on the shoulders of{" "}
              <span className="text-signal">people we trust.</span>
            </h2>
          </R>

          <div className="mt-16 grid gap-6 md:grid-cols-2">
            {ADVISORS.map((a) => (
              <AdvisorCard key={a.name} {...a} />
            ))}
          </div>
        </div>
      </section>

      {/* 05 · CTA STRIP */}
      <section
        data-testid="about-cta"
        className="relative overflow-hidden px-6 py-24 md:px-12 md:py-32"
      >
        {/* Western Ghats calming background */}
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src="/assets/wghats_ridges.jpg"
            alt=""
            data-testid="about-cta-bg"
            className="h-full w-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/70 to-ink/95" />
          <div className="absolute inset-0 bg-ink/30" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1200px] text-center">
          <R>
            <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
              06 · JOIN THE SIGNAL
            </p>
            <h3
              data-testid="about-cta-headline"
              className="mt-6 font-display text-3xl font-light leading-tight text-white md:text-5xl"
            >
              A vision. A mission. A promise. Now{" "}
              <span className="text-signal">it&apos;s your turn.</span>
            </h3>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
              <a
                href="https://xsdfem-1j.myshopify.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-cta-get"
                className="bg-white px-8 py-4 font-mono text-[11px] tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white"
              >
                GET TRIFIT
              </a>
              <a
                href="/science"
                data-testid="about-cta-science"
                className="font-mono text-[10px] tracking-[0.2em] text-fog underline-offset-4 hover:text-signal hover:underline"
              >
                THE SCIENCE →
              </a>
            </div>
          </R>
        </div>
      </section>
    </main>
  );
};

export default About;
